// =============================================================================
// Anomaly Center — ops-health findings from the reconciliation sweeps
// -----------------------------------------------------------------------------
// Lists OrderAnomaly rows (paid orders without designs, stuck orders, stale
// failed exports, failed summary PDFs, poison webhooks) plus virtual
// RECONCILE_TICK_OVERDUE rows synthesized by the API-side watchdog when a
// workers tick stops reporting. Staff can acknowledge, resolve (with note)
// or permanently ignore an anomaly.
// =============================================================================

import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@tkpod/api-client';
import {
  Badge,
  Button,
  Card,
  Modal,
  ModalFooter,
  Select,
  Spinner,
  useConfirm,
  type BadgeVariant,
} from '@tkpod/ui';
import { toast } from '@/lib/toast';

// ---------------------------------------------------------------------------
// Types (mirror the admin anomalies API)
// ---------------------------------------------------------------------------

interface AnomalyOrder {
  id: string;
  orderNumber: string;
  status: string;
  organizationId: string;
  connection: { id: string; name: string; externalDomain: string } | null;
}

interface CandidateDesign {
  designId: string;
  designRefId: string | null;
  createdAt: string;
  matchedPartKeys: string[];
}

interface AnomalyRow {
  id: string;
  virtual?: boolean;
  type: string;
  severity: 'CRITICAL' | 'WARNING' | 'INFO';
  status: 'OPEN' | 'ACKNOWLEDGED' | 'RESOLVED' | 'IGNORED';
  dedupeKey: string;
  orderId: string | null;
  organizationId: string | null;
  details: Record<string, unknown>;
  firstDetectedAt: string;
  lastSeenAt: string;
  resolvedAt?: string | null;
  resolutionNote?: string | null;
  order?: AnomalyOrder | null;
}

interface AnomalyListResponse {
  items: AnomalyRow[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

// ---------------------------------------------------------------------------
// Display maps
// ---------------------------------------------------------------------------

const TYPE_LABELS: Record<string, string> = {
  PAID_ORDER_NO_DESIGN: 'Paid order, no design',
  ORDER_STUCK_NEW: 'Order stuck in NEW',
  EXPORT_FAILED_STALE: 'Export failed (stale)',
  SUMMARY_PDF_FAILED: 'Summary PDF failed',
  WEBHOOK_POISON: 'Poison webhook',
  ORDER_INGEST_FAILED: 'Order ingest failed',
  PROVIDER_DISPATCH_STALLED: 'Provider dispatch stalled',
  SHIPMENT_LABEL_STALLED: 'Shipment label stalled',
  PROVIDER_SUBMISSION_STALLED: 'Provider submission stalled',
  RECONCILE_TICK_OVERDUE: 'Reconcile tick overdue',
  WHOLESALE_COST_ZERO: 'Wholesale cost missing',
  GDPR_PROVIDER_NOTIFY: 'GDPR provider notify',
  STRIPE_WEBHOOK_REJECTED: 'Stripe webhook rejected',
  OPTION_LINE_MISMATCH: 'Option line mismatch',
  OPTIONS_LINE_WITHOUT_SUBMISSION: 'Options line missing submission',
  OPTION_UPLOAD_NOT_READY: 'Option file not ready',
};

const SEVERITY_VARIANT: Record<string, BadgeVariant> = {
  CRITICAL: 'danger',
  WARNING: 'warning',
  INFO: 'info',
};

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  OPEN: 'danger',
  ACKNOWLEDGED: 'warning',
  RESOLVED: 'success',
  IGNORED: 'default',
};

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'OPEN', label: 'Open' },
  { value: 'ACKNOWLEDGED', label: 'Acknowledged' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'IGNORED', label: 'Ignored' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  ...Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label })),
];

const SEVERITY_OPTIONS = [
  { value: '', label: 'All severities' },
  { value: 'CRITICAL', label: 'Critical' },
  { value: 'WARNING', label: 'Warning' },
  { value: 'INFO', label: 'Info' },
];

function formatDuration(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AnomaliesPage() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('OPEN');
  const [type, setType] = useState('');
  const [severity, setSeverity] = useState('');
  const [resolveTarget, setResolveTarget] = useState<AnomalyRow | null>(null);

  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', '25');
  if (status) params.set('status', status);
  if (type) params.set('type', type);
  if (severity) params.set('severity', severity);

  const listQuery = useQuery<AnomalyListResponse>({
    queryKey: ['admin', 'anomalies', page, status, type, severity],
    queryFn: () =>
      api.get<AnomalyListResponse>(`/api/v1/admin/anomalies?${params}`),
    refetchInterval: 30_000,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'anomalies'] });
    void queryClient.invalidateQueries({
      queryKey: ['admin', 'anomaly-summary'],
    });
  };

  const actionMutation = useMutation({
    mutationFn: (input: { id: string; action: string; note?: string }) =>
      api.patch(`/api/v1/admin/anomalies/${input.id}`, {
        action: input.action,
        note: input.note,
      }),
    onSuccess: (_data, input) => {
      toast(`Anomaly ${input.action}d`, 'success');
      setResolveTarget(null);
      invalidate();
    },
    onError: (err) => {
      toast(err instanceof Error ? err.message : 'Action failed', 'error');
    },
  });

  async function handleIgnore(row: AnomalyRow) {
    const ok = await confirm({
      title: 'Ignore anomaly',
      description:
        'Ignoring is permanent — future sweeps will never re-open this finding. Use Resolve if the underlying issue was actually fixed.',
      confirmLabel: 'Ignore permanently',
      tone: 'danger',
    });
    if (ok) actionMutation.mutate({ id: row.id, action: 'ignore' });
  }

  const rows = listQuery.data?.items ?? [];
  const meta = listQuery.data?.meta;

  return (
    <div className="space-y-6">
      {dialog}
      <Card padding="none">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <div>
            <h1 className="text-lg font-semibold text-text-primary">
              Anomaly Center
            </h1>
            <p className="text-sm text-text-secondary">
              Findings from the order-health, ingest and webhook reconciliation
              sweeps. Auto-resolves when a condition clears.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-40">
              <Select
                aria-label="Status filter"
                options={STATUS_OPTIONS}
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div className="w-52">
              <Select
                aria-label="Type filter"
                options={TYPE_OPTIONS}
                value={type}
                onChange={(e) => {
                  setType(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div className="w-40">
              <Select
                aria-label="Severity filter"
                options={SEVERITY_OPTIONS}
                value={severity}
                onChange={(e) => {
                  setSeverity(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <Button
              variant="secondary"
              onClick={() => listQuery.refetch()}
              disabled={listQuery.isFetching}
            >
              Refresh
            </Button>
          </div>
        </div>

        {listQuery.isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Spinner />
          </div>
        ) : listQuery.isError ? (
          <p className="px-5 py-6 text-sm text-danger">
            {listQuery.error instanceof Error
              ? listQuery.error.message
              : 'Unable to load anomalies.'}
          </p>
        ) : rows.length === 0 ? (
          <p className="px-5 py-14 text-center text-sm text-text-secondary">
            No anomalies match the current filters — all clear.
          </p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border bg-surface-tertiary text-xs uppercase tracking-wide text-text-tertiary">
              <tr>
                <th className="px-5 py-2 font-semibold">Type</th>
                <th className="px-5 py-2 font-semibold">Severity</th>
                <th className="px-5 py-2 font-semibold">Status</th>
                <th className="px-5 py-2 font-semibold">Order / Target</th>
                <th className="px-5 py-2 font-semibold">Detected</th>
                <th className="px-5 py-2 font-semibold">Last seen</th>
                <th className="px-5 py-2 font-semibold">Details</th>
                <th className="px-5 py-2 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <AnomalyTableRow
                  key={row.id}
                  row={row}
                  busy={actionMutation.isPending}
                  onAcknowledge={() =>
                    actionMutation.mutate({ id: row.id, action: 'acknowledge' })
                  }
                  onResolve={() => setResolveTarget(row)}
                  onIgnore={() => void handleIgnore(row)}
                />
              ))}
            </tbody>
          </table>
        )}

        {meta && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-5 py-3 text-sm">
            <span className="text-text-tertiary">
              Page {meta.page} of {meta.totalPages} • {meta.total} total
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      <ResolveModal
        row={resolveTarget}
        loading={actionMutation.isPending}
        onClose={() => setResolveTarget(null)}
        onSubmit={(note) =>
          resolveTarget &&
          actionMutation.mutate({
            id: resolveTarget.id,
            action: 'resolve',
            note: note || undefined,
          })
        }
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

function AnomalyTableRow({
  row,
  busy,
  onAcknowledge,
  onResolve,
  onIgnore,
}: {
  row: AnomalyRow;
  busy: boolean;
  onAcknowledge: () => void;
  onResolve: () => void;
  onIgnore: () => void;
}) {
  const actionable =
    !row.virtual && (row.status === 'OPEN' || row.status === 'ACKNOWLEDGED');

  return (
    <tr className="border-b border-border/60 align-top">
      <td className="px-5 py-3">
        <span className="font-medium text-text-primary">
          {TYPE_LABELS[row.type] ?? row.type}
        </span>
        {row.virtual && (
          <Badge variant="purple" size="sm" className="ml-2">
            watchdog
          </Badge>
        )}
      </td>
      <td className="px-5 py-3">
        <Badge variant={SEVERITY_VARIANT[row.severity] ?? 'default'} size="sm">
          {row.severity}
        </Badge>
      </td>
      <td className="px-5 py-3">
        <Badge variant={STATUS_VARIANT[row.status] ?? 'default'} size="sm">
          {row.status}
        </Badge>
      </td>
      <td className="px-5 py-3 text-xs">
        {row.order ? (
          <div className="space-y-0.5">
            <Link
              to={`/orders/${row.order.id}`}
              className="font-medium text-brand-600 hover:underline"
            >
              {row.order.orderNumber}
            </Link>
            <p className="text-text-tertiary">
              {row.order.status}
              {row.order.connection
                ? ` • ${row.order.connection.name}`
                : ''}
            </p>
          </div>
        ) : row.virtual ? (
          <code className="font-mono text-text-secondary">
            {String(row.details.runType ?? '')}
          </code>
        ) : (
          <span className="text-text-tertiary">—</span>
        )}
      </td>
      <td className="px-5 py-3 text-xs text-text-secondary">
        {new Date(row.firstDetectedAt).toLocaleString()}
      </td>
      <td className="px-5 py-3 text-xs text-text-secondary">
        {new Date(row.lastSeenAt).toLocaleString()}
      </td>
      <td className="max-w-md px-5 py-3 text-xs text-text-secondary">
        <AnomalyDetails row={row} />
      </td>
      <td className="px-5 py-3 text-right">
        {actionable && (
          <div className="flex justify-end gap-1.5">
            {row.status === 'OPEN' && (
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={onAcknowledge}
              >
                Acknowledge
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={onResolve}
            >
              Resolve
            </Button>
            <Button variant="ghost" size="sm" disabled={busy} onClick={onIgnore}>
              Ignore
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Type-aware details rendering
// ---------------------------------------------------------------------------

function AnomalyDetails({ row }: { row: AnomalyRow }) {
  const d = row.details;

  if (row.type === 'RECONCILE_TICK_OVERDUE') {
    const overdueMs = Number(d.overdueMs ?? 0);
    const intervalMs = Number(d.intervalMs ?? 0);
    return (
      <p>
        Last run {String(d.lastRunStatus ?? '?')} at{' '}
        {d.lastRunAt ? new Date(String(d.lastRunAt)).toLocaleString() : '?'} —{' '}
        <span className="font-medium text-danger">
          {formatDuration(overdueMs)} overdue
        </span>{' '}
        (interval {formatDuration(intervalMs)}). The workers process may be
        down.
      </p>
    );
  }

  if (row.type === 'PAID_ORDER_NO_DESIGN') {
    const candidates = Array.isArray(d.candidateDesigns)
      ? (d.candidateDesigns as CandidateDesign[])
      : [];
    const unlinked = Array.isArray(d.unlinkedItemIds)
      ? d.unlinkedItemIds.length
      : 0;
    return (
      <div className="space-y-1">
        <p>
          {unlinked} line{unlinked === 1 ? '' : 's'} without design linkage.
        </p>
        {candidates.length > 0 ? (
          <div>
            <p className="font-medium text-text-primary">
              Candidate designs ({candidates.length}):
            </p>
            <ul className="mt-0.5 space-y-0.5">
              {candidates.slice(0, 3).map((c) => (
                <li key={c.designId} className="font-mono text-[11px]">
                  <Link
                    to={`/designs/${c.designRefId ?? c.designId}`}
                    className="text-brand-600 hover:underline"
                  >
                    {c.designRefId ?? c.designId}
                  </Link>{' '}
                  • {new Date(c.createdAt).toLocaleTimeString()}
                </li>
              ))}
              {candidates.length > 3 && (
                <li className="text-text-tertiary">
                  +{candidates.length - 3} more on the order page
                </li>
              )}
            </ul>
          </div>
        ) : (
          <p className="text-text-tertiary">
            No orphan design candidates matched yet.
          </p>
        )}
      </div>
    );
  }

  // Generic fallback: compact key/value dump (details never contain PII).
  const entries = Object.entries(d).filter(
    ([, v]) => v !== null && typeof v !== 'object',
  );
  if (entries.length === 0) {
    return <span className="text-text-tertiary">—</span>;
  }
  return (
    <p className="font-mono text-[11px]">
      {entries.map(([k, v]) => `${k}=${String(v)}`).join(' • ')}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Resolve modal (note input)
// ---------------------------------------------------------------------------

function ResolveModal({
  row,
  loading,
  onClose,
  onSubmit,
}: {
  row: AnomalyRow | null;
  loading: boolean;
  onClose: () => void;
  onSubmit: (note: string) => void;
}) {
  const [note, setNote] = useState('');

  return (
    <Modal
      open={!!row}
      onClose={loading ? () => {} : onClose}
      title="Resolve anomaly"
      description="Marks this finding as manually resolved. Add a short note describing the fix so the history stays useful."
      size="sm"
    >
      <div>
        <label
          htmlFor="anomaly-resolve-note"
          className="mb-1 block text-xs font-medium uppercase tracking-wide text-text-tertiary"
        >
          Resolution note (optional)
        </label>
        <textarea
          id="anomaly-resolve-note"
          rows={3}
          maxLength={1000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={loading}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-brand-600 focus:outline-none"
          placeholder="e.g. Attached design dsg_abc123 via order detail"
        />
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={() => onSubmit(note.trim())} disabled={loading}>
          {loading ? 'Resolving…' : 'Resolve'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
